import { Button, Modal, Select, TextInput } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../../Reducers/store';
import VendorApi from '../../Api/VendorApi';
import { Inventory } from '../../Models/Inventory';
import {
  formatDateInputs,
  formatNumbers,
  onlyNumberInput,
  onlyNumberInputRaw,
} from '../../Utils';
import {
  fetchInventoryTypes,
  fetchUsers,
  fetchVendors,
} from '../../Actions/CommonFields';
import InventoryApi from '../../Api/InventoryApi';

export default function InventoryModal({
  show,
  setShow,
  getInventory,
  selectedInventory,
}: {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  getInventory: () => Promise<void> | undefined;
  selectedInventory: Inventory | null;
}) {
  const dispatch = useAppDispatch();
  const { users, inventoryTypes, vendors } = useAppSelector(
    (state) => state.commonFields
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);

  const [formData, setFormData] = useState<Inventory>({
    id: 0,
    name: null,
    typeId: null,
    originalQuantity: null,
    cost: null,
    vendorId: 38,
    dateOfPurchase: null,
    purchasedBy: null,
    quantityRemaining: null,

    type: null,
    vendor: null,
  });

  const getReferences = async () => {
    if (!users.length || !inventoryTypes.length || !vendors.length) {
      await Promise.all([
        dispatch(fetchUsers()),
        dispatch(fetchInventoryTypes()),
        dispatch(fetchVendors()),
      ]);
    }
  };

  useEffect(() => {
    if (show === true) {
      getReferences();
      handleSetInitialValues();
    }
  }, [show]);

  const handleSetInitialValues = () => {
    const values = selectedInventory
      ? {
          ...selectedInventory,
        }
      : {
          id: 0,
          name: null,
          typeId: null,
          originalQuantity: null,
          cost: null,
          vendorId: 38,
          dateOfPurchase: null,
          purchasedBy: null,
          quantityRemaining: null,

          type: null,
          vendor: null,
        };
    setFormData(values as Inventory);
  };

  const handleResetFormValues = () => {
    const values = {
      id: 0,
      name: null,
      typeId: null,
      originalQuantity: null,
      cost: null,
      vendorId: null,
      dateOfPurchase: null,
      purchasedBy: null,
      quantityRemaining: null,

      type: null,
      vendor: null,
    };
    setFormData(values as Inventory);
    setButtonDisabled(false);
  };

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };
  const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };
  const handleChangeCurrencyInput = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    const formattedValue = formatNumbers(value);
    setFormData((prevData) => ({ ...prevData, [name]: formattedValue }));
  };

  function cleanMoneyValues(formData: Inventory) {
    formData.cost = Number((formData.cost ?? 0).toString().replace(',', ''));
  }
  const onSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    setButtonDisabled(true);
    setIsLoading(true);
    cleanMoneyValues(formData);
    if (!formData.typeId) {
      formData.typeId = null;
    }
    if (!formData.dateOfPurchase) {
      formData.dateOfPurchase = null;
    }
    return await InventoryApi.addUpdateInventory(formData)
      .then((res) => {
        if (res.data.success) {
          toast.success('Save Success');
          setIsLoading(false);
          setButtonDisabled(false);
          handleResetFormValues();
          getInventory();
          onCloseModal();
        } else {
          toast.error(res.data.message);
          setIsLoading(false);
          setButtonDisabled(false);
        }
      })
      .catch((err) => {
        toast.error('Error: ' + err);
        setIsLoading(false);
        setButtonDisabled(false);
      });
  };

  function onCloseModal() {
    handleResetFormValues();
    setShow(false);
  }

  return (
    <Modal show={show} size='2xl' onClose={onCloseModal} popup>
      <Modal.Header></Modal.Header>
      <Modal.Body>
        <div className='w-full'>
          <div className='w-full'>
            <div className='mx-1 flex items-end justify-center'>
              <div className='flex flex-1 justify-center'>
                <h1 className='text-gray-900 font-bold text-4xl'>
                  {selectedInventory ? 'Edit' : 'Add'} Inventory
                </h1>
              </div>
            </div>
          </div>
          <div>
            <div className='container mx-auto p-2'>
              <div className='md:flex no-wrap md:-mx-2 '>
                <div className='w-full'>
                  <div className='bg-white'>
                    <hr className='bg-gray-300 h-[5px]'></hr>
                    <form id='vendorInventoryForm' onSubmit={onSubmit}>
                      <div className='text-gray-500'>
                        <div className='grid grid-cols-[35%_65%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Vendor:
                            </div>
                          </div>
                          <div className='w-full'>
                            <Select
                              id='vendorId'
                              name='vendorId'
                              onChange={handleChangeSelect}
                              value={formData.vendorId ?? ''}
                              sizing='md'
                              required
                              disabled
                            >
                              <option value={''}>(Select)</option>
                              {vendors.map((type) => (
                                <option key={type.id} value={type.id}>
                                  {type.name}
                                </option>
                              ))}
                            </Select>
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>

                        <div className='grid grid-cols-[35%_65%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Name:
                            </div>
                          </div>
                          <div className='w-full'>
                            <TextInput
                              id='name'
                              name='name'
                              type='text'
                              placeholder='Name...'
                              onChange={handleChangeInput}
                              value={formData.name ?? ''}
                              sizing='md'
                              required
                            />
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[35%_65%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Type:
                            </div>
                          </div>
                          <div className='w-full'>
                            <Select
                              id='typeId'
                              name='typeId'
                              onChange={handleChangeSelect}
                              value={formData.typeId ?? ''}
                              sizing='md'
                              required
                            >
                              <option value={''}>(Select)</option>
                              {inventoryTypes.map((type) => (
                                <option key={type.id} value={type.id}>
                                  {type.name}
                                </option>
                              ))}
                            </Select>
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[35%_65%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Quantity:
                            </div>
                          </div>
                          <div className='w-full'>
                            <TextInput
                              id='quantityRemaining'
                              name='quantityRemaining'
                              type='text'
                              placeholder=''
                              onChange={handleChangeInput}
                              onKeyDown={(event) => {
                                if (onlyNumberInputRaw(event)) {
                                  event.preventDefault();
                                }
                              }}
                              value={formData.quantityRemaining ?? ''}
                              sizing='md'
                            />
                          </div>
                        </div>
                      </div>
                      <div className='flex justify-end pt-2'>
                        <Button
                          disabled={buttonDisabled}
                          type='submit'
                          className='buttonMain'
                          form='vendorInventoryForm'
                          // onClick={() => onSubmit()}
                        >
                          Save
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
